import { Autocomplete, Chip, TextField } from '@mui/material';
import { useMemo } from 'react';
import { useEntities } from '../../api/queries/entities';

interface Props {
  bookId: string;
  value: string[];
  onChange: (ids: string[]) => void;
  label?: string;
}

/**
 * Multi-select over book-scoped + private-library character entities. Used in paragraph
 * metadata to populate Paragraph.SpeakingEntityIds. Phase 1 had a stubbed free-text box;
 * Phase 2 W9 wires it to real entities.
 */
export function SpeakingEntityPicker({
  bookId,
  value,
  onChange,
  label = 'Speaking entities',
}: Props) {
  const privateQuery = useEntities({
    scope: 'private',
    type: 'character',
    projectId: bookId,
    pageSize: 200,
  });
  const bookQuery = useEntities({
    scope: 'book',
    type: 'character',
    projectId: bookId,
    pageSize: 200,
  });

  const options = useMemo(() => {
    const byId = new Map<string, { id: string; name: string }>();
    for (const e of privateQuery.data?.items ?? [])
      byId.set(e.id, { id: e.id, name: e.name });
    for (const e of bookQuery.data?.items ?? [])
      byId.set(e.id, { id: e.id, name: e.name });
    return Array.from(byId.values());
  }, [privateQuery.data, bookQuery.data]);

  const selected = options.filter((o) => value.includes(o.id));
  // If value contains IDs not yet loaded, represent them by id-only stubs.
  const missing = value
    .filter((id) => !options.some((o) => o.id === id))
    .map((id) => ({ id, name: `[${id.slice(0, 6)}…]` }));

  return (
    <Autocomplete
      multiple
      size="small"
      options={options}
      value={[...selected, ...missing]}
      onChange={(_, next) => onChange(next.map((n) => n.id))}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionLabel={(opt) => opt.name}
      renderTags={(tags, getTagProps) =>
        tags.map((tag, i) => {
          const { key, ...rest } = getTagProps({ index: i });
          return (
            <Chip key={tag.id || key} size="small" label={tag.name} {...rest} />
          );
        })
      }
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );
}
